import scribe from '@grab-x/scribe-web-sdk';
import 'babel-polyfill';
import i18n from 'i18n-js';
import _ from 'lodash';
import moment from 'moment';
import ga from 'react-ga';
import * as Sentry from 'utils/sentry';

import fastclick from 'fastclick';
import grabStorage from 'grab-storage';
import jwtDecode from 'jwt-decode';
import React from 'react';
import ReactDOM from 'react-dom';

import { Provider } from 'react-redux';
import ReduxToastr from 'react-redux-toastr';
import { Router, browserHistory } from 'react-router';
import { syncHistoryWithStore } from 'react-router-redux';
import configureStore from 'stores/configure-store';

import { getConfig, setHasDetailsHeader } from 'actions/app';
import {
  cookieLogin,
  forceRelogin,
  refreshToken as refreshTokenAction,
} from 'actions/user';
import Config, {
  RELOGIN_THRESHOLD,
  TOASTR_TIMEOUT,
  TOKEN_CHECK_INTERVAL,
  storageStateKey,
} from 'config';
import routes from 'routes';
import { getAuthToken } from 'utils/user-utils';
import enLocale from '../locales/en.json';

import 'global.scss';
import ScribeEventName from './constants/scribe';
import { initializeScribe } from './utils/scribe';

// We currently only support english locale
i18n.translations[Config.DEFAULT_LOCALE] = enLocale;
i18n.locale = Config.DEFAULT_LOCALE;

const store = configureStore(
  JSON.parse(grabStorage.getItem(storageStateKey)) || {},
);

store.subscribe(
  _.throttle(() => {
    const storeState = store.getState();
    grabStorage.setItem(
      storageStateKey,
      JSON.stringify({
        app: { config: storeState.app.config },
      }),
    );
  }, 1000),
);
// Mobile Click Support
fastclick.attach(document.body);

// Route Settings
function routeChange() {
  document.body.scrollTop = 0;
  ga.pageview(window.location.pathname);
  scribe.sendEvent(ScribeEventName.NAVIGATION);
}

function checkExpiringToken() {
  const state = store.getState();
  const { accessToken, refreshToken } = state.app.user;
  console.log(state.app.user);
  if (accessToken && refreshToken) {
    const tokenPayload = jwtDecode(accessToken);
    const currentTimestamp = moment().unix();
    const diff = tokenPayload.exp - currentTimestamp;

    // if token has expired, force relogin
    if (diff < 0) {
      store.dispatch(forceRelogin());
      // ask whether wants to stay logged in or not
    } else if (diff < RELOGIN_THRESHOLD) {
      // eslint-disable-next-line no-alert
      const ok = confirm(
        'Your session is expiring soon. Do you want to stay logged in?',
      );
      if (ok) {
        store.dispatch(refreshTokenAction(accessToken, refreshToken));
      } else {
        store.dispatch(forceRelogin());
      }
    }
  }
}

/**
 * Checks auth cookie and dispatches a login action.
 * Cookies are safe to dispatch store action regardless because
 * they automatically expire and reupdate on token refresh actions.
 */
function checkAuthCookie() {
  const state = store.getState();
  const { accessToken, refreshToken, authType } = getAuthToken();
  const isGoogleLogin = authType === 'google';

  if (
    !accessToken ||
    (!refreshToken && !isGoogleLogin) || // Google login doesn't have refresh token
    state.app.user.accessToken === accessToken
  ) {
    return;
  }

  store.dispatch(cookieLogin(accessToken, refreshToken));
}

const history = syncHistoryWithStore(browserHistory, store);

function renderApp() {
  ReactDOM.render(
    <Provider store={store}>
      <div>
        <Router history={history} onUpdate={routeChange}>
          {routes()}
        </Router>
        <ReduxToastr
          timeOut={TOASTR_TIMEOUT}
          preventDuplicates
          newestOnTop={false}
          position="top-right"
          transitionIn="fadeIn"
          transitionOut="fadeOut"
          progressBar
        />
      </div>
    </Provider>,
    document.getElementById('app'),
  );
}

function initialize() {
  // Login with cookies if available
  checkAuthCookie();

  const { GA_KEY, APP_VERSION, SENTRY_DSN } = window.Grab.config;
  // GA
  ga.initialize(GA_KEY, {
    debug: grabStorage.reactGa === 'true',
  });

  // Get Sentry
  Sentry.init(SENTRY_DSN, APP_VERSION);

  // Check expiring token every TOKEN_CHECK_INTERVAL ms
  if (process.env.NODE_ENV !== 'development') {
    setInterval(() => {
      checkExpiringToken();
    }, TOKEN_CHECK_INTERVAL);
  }

  // Get feature flags
  if (_.get(window.Grab.config, 'IS_OMEGA_BACKEND_AVAILABLE', true)) {
    store.dispatch(getConfig());
  }

  // Set the details header to false before every route change
  browserHistory.listenBefore(() => {
    store.dispatch(setHasDetailsHeader(false));
  });

  const state = store.getState();
  initializeScribe(_.get(state, 'app.user.accessToken'));
}

_.set(window, 'Grab.storage', grabStorage);
_.set(window, 'Grab.scribe', scribe);

Config.getConfigFile().then(() => {
  initialize();
  renderApp();
});
